// User profile popout — icon button that opens a small panel with
// the signed-in email, a Settings link, and Sign out. Replaces the
// top-bar session cluster now that the top bar is gone.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { clearSession, getUser } from "../api/client";

export function UserPopout() {
  const navigate = useNavigate();
  const user = getUser();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function signOut() {
    clearSession();
    navigate("/signin");
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account"
        title={user?.display_name ?? user?.email ?? "Account"}
        className={`relative cursor-pointer transition-colors ${
          open ? "text-primary" : "text-muted hover:text-primary"
        }`}
        data-testid="user-popout-toggle"
      >
        <User size={14} className="block" />
        {open && (
          <span
            aria-hidden
            className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
          />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-3 w-60 rounded-md border border-edge bg-surface-raised p-3 shadow-[var(--wt-shadow)]"
          data-testid="user-popout"
        >
          <div className="flex flex-col gap-0.5 border-b border-edge px-2 pb-2">
            <span className="text-xs text-faint">Signed in as</span>
            <span className="truncate text-sm font-semibold text-primary">
              {user?.display_name ?? "—"}
            </span>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              navigate({ search: "?tab=settings" });
            }}
            className="mt-2 w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-sm text-muted transition-colors hover:bg-surface hover:text-primary"
            data-testid="user-popout-settings"
          >
            Settings
          </button>
          <button
            onClick={signOut}
            className="w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-sm text-muted transition-colors hover:bg-surface hover:text-danger"
            data-testid="user-popout-signout"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
